/**
 * Store links + per-device routing for the web "Get the app" banner
 * (components/AppDownloadBanner.js).
 *
 * IMPORTANT: on the web PWA, `Capacitor.getPlatform()` always returns "web" and
 * cannot tell an iPhone browser from an Android browser — so platform detection
 * here is user-agent based, NOT Capacitor-based. (`getClientPlatform()` in
 * nativePayment.js is the Capacitor one and is the wrong tool for this.)
 *
 * Store identities are verified against source-of-truth:
 *   - Android package — android-config/google-services.json + android-release.yml
 *   - iOS App Store id — iTunes lookup API (seller "Trulink Hospitality Services")
 */

export const APP_STORE_ID = '6774176938';
export const APP_STORE_URL = `https://apps.apple.com/in/app/penguin-bean-coffee/id${APP_STORE_ID}`;
export const ANDROID_PACKAGE = 'com.penguinbeancoffee.app';
export const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;

/**
 * Detect the visitor's mobile OS from the user agent.
 * @returns {'ios'|'android'|'desktop'}
 */
export function detectMobileOS() {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  const noMSStream = typeof window === 'undefined' || !window.MSStream;
  if (/iPad|iPhone|iPod/.test(ua) && noMSStream) return 'ios';
  // iPadOS 13+ masquerades as macOS Safari; the touch check distinguishes a real
  // iPad from a real Mac (Apple ships no touchscreen Macs, so a touch-capable
  // "Macintosh" is an iPad).
  if (/Macintosh/.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

/**
 * Resolve the store CTA for the current visitor, or `null` when there's nothing
 * to offer (desktop / unknown — Phase 1 shows no banner there).
 * @returns {{platform:'ios'|'android', url:string, store:string}|null}
 */
export function getStoreTarget() {
  switch (detectMobileOS()) {
    case 'ios':
      return { platform: 'ios', url: APP_STORE_URL, store: 'App Store' };
    case 'android':
      return { platform: 'android', url: PLAY_STORE_URL, store: 'Google Play' };
    default:
      return null;
  }
}

/**
 * Public one-link-for-both-stores install page (frontend/website/app.html).
 *
 * This is the value encoded in the printed/shared install QR, so it is pinned to
 * the PRODUCTION apex on purpose — never an env-derived origin. A staging build
 * of the admin panel must still generate a QR that works on a real table tent.
 */
export const APP_DOWNLOAD_URL = 'https://penguinbeancoffee.com/app';
